/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  FiTool,
  FiBell,
  FiClipboard,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
  FiPlus,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiCamera,
  FiInfo,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  getMaintenanceReminders,
  approveReminder,
  ignoreReminder,
  getWorkOrders,
  createWorkOrder,
  updateWorkOrderStatus,
  completeWorkOrder,
  getSupplierDeviceItems,
} from "../../service/ApiService/MaintenanceApi";

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { key: "reminders", label: "Nhắc nhở bảo trì", icon: FiBell },
  { key: "workorders", label: "Lệnh bảo trì", icon: FiClipboard },
];

const WO_STATUS_LABELS = {
  PENDING: "Đang chờ",
  IN_PROGRESS: "Đang thực hiện",
  PENDING_REVIEW: "Chờ nghiệm thu",
  INFO_REQUIRED: "Cần bổ sung",
  COMPLETED: "Hoàn tất",
  CANCELLED: "Đã hủy",
};

const WO_STATUS_STYLES = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  IN_PROGRESS: "bg-blue-50 text-blue-700 border-blue-200",
  PENDING_REVIEW: "bg-purple-50 text-purple-700 border-purple-200",
  INFO_REQUIRED: "bg-rose-50 text-rose-700 border-rose-200",
  COMPLETED: "bg-green-50 text-green-700 border-green-200",
  CANCELLED: "bg-slate-50 text-slate-500 border-slate-200",
};

const WO_TYPE_LABELS = {
  PREVENTIVE: "Phòng ngừa",
  CORRECTIVE: "Sửa chữa (Sự cố)",
};

const WO_TYPE_STYLES = {
  PREVENTIVE: "bg-indigo-50 text-indigo-700 border-indigo-200",
  CORRECTIVE: "bg-rose-50 text-rose-700 border-rose-200",
};

const TRIGGER_TYPE_LABELS = {
  RENTAL_COUNT: "Số lần thuê",
  DATE_INTERVAL: "Khoảng thời gian",
  NEXT_DUE: "Quá hạn",
};

const PRIORITY_LABELS = {
  LOW: "Thấp",
  MEDIUM: "Trung bình",
  HIGH: "Cao",
};

const PRIORITY_STYLES = {
  LOW: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MEDIUM: "bg-amber-50 text-amber-700 border-amber-200",
  HIGH: "bg-rose-50 text-rose-700 border-rose-200",
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const today = () => {
  const d = new Date();
  return d.toISOString().split("T")[0];
};

const DeviceSelector = ({ items, value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("ALL"); // ALL, AVAILABLE, REPAIR
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Không hiển thị thiết bị đang bảo trì
      if (item.status === "MAINTENANCE") return false;

      if (filterType === "AVAILABLE" && item.status !== "AVAILABLE") return false;
      if (filterType === "REPAIR" && item.status === "AVAILABLE") return false;

      const searchLower = String(search).toLowerCase();
      const nameMatch = String(item.device?.name || "").toLowerCase().includes(searchLower);
      const serialMatch = String(item.internalCode || item.serialNumber || "").toLowerCase().includes(searchLower);
      return nameMatch || serialMatch;
    });
  }, [items, search, filterType]);

  const selectedItem = items.find((i) => i._id === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white cursor-pointer flex items-center justify-between ${disabled ? "opacity-50 pointer-events-none" : ""
          }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedItem ? (
          <div className="flex items-center gap-2 truncate flex-1">
            {selectedItem.device?.images?.[0] ? (
              <img
                src={selectedItem.device.images[0]}
                alt=""
                className="w-6 h-6 object-cover rounded-md flex-shrink-0"
              />
            ) : (
              <div className="w-6 h-6 bg-slate-100 rounded-md flex items-center justify-center flex-shrink-0">
                <FiTool size={10} className="text-slate-400" />
              </div>
            )}
            <div className="flex items-center gap-2 truncate">
              <span className="truncate font-medium">
                {selectedItem.device?.name || "Thiết bị"} —{" "}
                <span className="text-slate-500">{selectedItem.internalCode || selectedItem.serialNumber}</span>
              </span>
              {selectedItem.status !== "AVAILABLE" && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${selectedItem.status === "MAINTENANCE" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"
                  }`}>
                  {selectedItem.status === "MAINTENANCE" ? "Bảo trì" : "Sự cố"}
                </span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">— Chọn thiết bị —</span>
        )}
        <FiChevronDown className={`transition-transform text-slate-400 ${isOpen ? "rotate-180" : ""}`} size={16} />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-col max-h-[320px]">
          <div className="p-2 border-b border-slate-100 space-y-2">
            <input
              type="text"
              placeholder="Tìm kiếm tên, Serial..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoFocus
            />
            <div className="flex gap-1 p-1 bg-slate-50 rounded-lg">
              <button
                type="button"
                onClick={() => setFilterType("ALL")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${filterType === "ALL" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
              >
                Tất cả
              </button>
              <button
                type="button"
                onClick={() => setFilterType("AVAILABLE")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${filterType === "AVAILABLE"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                  }`}
              >
                Bình thường
              </button>
              <button
                type="button"
                onClick={() => setFilterType("REPAIR")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${filterType === "REPAIR" ? "bg-white text-rose-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
              >
                Cần sửa
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
            {filteredItems.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">Không tìm thấy thiết bị phù hợp</div>
            ) : (
              filteredItems.map((item) => {
                const isSelected = item._id === value;
                const devName = item.device?.name || "Thiết bị";
                const serial = item.internalCode || item.serialNumber || "N/A";

                let statusBadge = null;
                if (item.status === "AVAILABLE") {
                  statusBadge = (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                      Khả dụng
                    </span>
                  );
                } else if (item.status === "MAINTENANCE") {
                  statusBadge = (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                      Đang bảo trì
                    </span>
                  );
                } else {
                  statusBadge = (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700">
                      Sự cố
                    </span>
                  );
                }

                return (
                  <div
                    key={item._id}
                    onClick={() => {
                      onChange(item._id);
                      setIsOpen(false);
                    }}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-slate-50"
                      }`}
                  >
                    {item.device?.images?.[0] ? (
                      <img
                        src={item.device.images[0]}
                        alt=""
                        className="w-10 h-10 object-cover rounded-lg border border-slate-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200 flex-shrink-0">
                        <FiTool size={14} className="text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{devName}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-500">SN: {serial}</span>
                        {statusBadge}
                      </div>
                    </div>
                    {isSelected && <FiCheckCircle className="text-indigo-600 mt-1 flex-shrink-0" size={16} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SupplierMaintenance() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return ["reminders", "workorders"].includes(t) ? t : "reminders";
  });

  useEffect(() => {
    const newTab = searchParams.get("tab");
    if (newTab && ["reminders", "workorders"].includes(newTab)) {
      setActiveTab(newTab);
    }
  }, [location.search, searchParams]);

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);

  // WorkOrders state
  const [workOrders, setWorkOrders] = useState([]);
  const [woLoading, setWoLoading] = useState(false);
  const [woStats, setWoStats] = useState({ pending: 0, inProgress: 0, completed: 0, totalCost: 0 });
  const [woStatusFilter, setWoStatusFilter] = useState("ALL");
  const [woTypeFilter, setWoTypeFilter] = useState("ALL");
  const [woCurrentPage, setWoCurrentPage] = useState(1);
  const [woTotalPages, setWoTotalPages] = useState(1);
  const woItemsPerPage = 5;

  // Modals
  const [approveModal, setApproveModal] = useState(null); // reminder object
  const [createWoModal, setCreateWoModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(null); // workorder object
  const [lightboxImg, setLightboxImg] = useState(null);

  // Forms
  const [approveForm, setApproveForm] = useState({ scheduledDate: today(), notes: "" });
  const [createWoForm, setCreateWoForm] = useState({
    deviceItemId: "",
    maintenanceType: "PREVENTIVE",
    scheduledDate: today(),
    notes: "",
    priority: "LOW",
    estimatedCost: "",
    providerName: "",
    imagesBefore: [],
  });
  const [completeForm, setCompleteForm] = useState({ notes: "", cost: "", imagesAfter: [] });
  const [submitting, setSubmitting] = useState(false);

  // Danh sách DeviceItems của supplier (cho dropdown tạo WO thủ công)
  const [allDeviceItems, setAllDeviceItems] = useState([]);
  const [deviceItemsLoading, setDeviceItemsLoading] = useState(false);

  const loadSupplierDeviceItems = useCallback(async () => {
    setDeviceItemsLoading(true);
    try {
      const res = await getSupplierDeviceItems();
      setAllDeviceItems(Array.isArray(res?.data) ? res.data : (res?.data?.data || []));
    } catch {
      // silent — ko block
    } finally {
      setDeviceItemsLoading(false);
    }
  }, []);

  // ── Data Loaders ────────────────────────────────────────────────────────────
  const loadReminders = useCallback(async () => {
    setRemindersLoading(true);
    try {
      const res = await getMaintenanceReminders("PENDING");
      setReminders(res?.data?.data || res?.data || []);
    } catch {
      toast.error("Không thể tải danh sách nhắc nhở");
    } finally {
      setRemindersLoading(false);
    }
  }, []);

  const loadWorkOrders = useCallback(async (page = woCurrentPage) => {
    setWoLoading(true);
    try {
      const params = { page, limit: woItemsPerPage };
      if (woStatusFilter !== "ALL") params.status = woStatusFilter;
      if (woTypeFilter !== "ALL") params.maintenanceType = woTypeFilter;
      const res = await getWorkOrders(params);
      setWorkOrders(res?.data || []);
      setWoStats(res?.stats || { pending: 0, inProgress: 0, completed: 0, totalCost: 0 });
      const total = res?.total || res?.data?.total || 0;
      setWoTotalPages(Math.max(1, Math.ceil(total / woItemsPerPage)));
    } catch {
      toast.error("Không thể tải danh sách lệnh bảo trì");
    } finally {
      setWoLoading(false);
    }
  }, [woStatusFilter, woTypeFilter, woCurrentPage, woItemsPerPage]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  useEffect(() => {
    if (activeTab === "workorders") loadWorkOrders(woCurrentPage);
  }, [activeTab, loadWorkOrders, woCurrentPage]);

  // Reset page when filter changes
  useEffect(() => {
    setWoCurrentPage(1);
  }, [woStatusFilter, woTypeFilter]);

  const setTab = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };

  // ── Reminder Actions ────────────────────────────────────────────────────────
  const handleApproveSubmit = async () => {
    if (!approveModal) return;
    setSubmitting(true);
    try {
      await approveReminder(approveModal._id, approveForm);
      toast.success("Đã tạo lệnh bảo trì!");
      setApproveModal(null);
      setApproveForm({ scheduledDate: today(), notes: "" });
      loadReminders();
      loadWorkOrders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không thể duyệt nhắc nhở");
    } finally {
      setSubmitting(false);
    }
  };

  const handleIgnore = (reminder) => {
    const tId = toast(
      <div>
        <p className="font-medium text-slate-800 mb-3">Bỏ qua nhắc nhở bảo trì này?</p>
        <div className="flex gap-2 justify-end">
          <button
            className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-200 transition-colors"
            onClick={() => toast.dismiss(tId)}
          >
            Hủy
          </button>
          <button
            className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
            onClick={async () => {
              toast.dismiss(tId);
              try {
                await ignoreReminder(reminder._id);
                toast.success("Đã bỏ qua nhắc nhở bảo trì");
                loadReminders();
              } catch (err) {
                toast.error(err?.response?.data?.message || "Không thể bỏ qua");
              }
            }}
          >
            Đồng ý
          </button>
        </div>
      </div>,
      { autoClose: false, closeOnClick: false, closeButton: false }
    );
  };

  // ── WorkOrder Actions ────────────────────────────────────────────
  const handleCreateWo = async () => {
    if (!createWoForm.deviceItemId) {
      toast.warning("Vui lòng chọn thiết bị");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("deviceItemId", createWoForm.deviceItemId);
      fd.append("maintenanceType", createWoForm.maintenanceType);
      fd.append("scheduledDate", createWoForm.scheduledDate);
      fd.append("notes", createWoForm.notes || "");
      fd.append("priority", createWoForm.priority);
      fd.append("estimatedCost", createWoForm.estimatedCost || "0");
      fd.append("providerName", createWoForm.providerName || "");
      createWoForm.imagesBefore.forEach((f) => fd.append("imagesBefore", f));

      await createWorkOrder(fd);
      toast.success("Đã tạo lệnh bảo trì!");
      setCreateWoModal(false);
      setCreateWoForm({
        deviceItemId: "",
        maintenanceType: "PREVENTIVE",
        scheduledDate: today(),
        notes: "",
        priority: "LOW",
        estimatedCost: "",
        providerName: "",
        imagesBefore: [],
      });
      loadWorkOrders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không thể tạo lệnh bảo trì");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = (wo, status) => {
    const confirmMsg =
      status === "IN_PROGRESS" ? "Bắt đầu thực hiện lệnh bảo trì này?" : "Hủy lệnh bảo trì này?";

    const tId = toast(
      <div>
        <p className="font-medium text-slate-800 mb-3">{confirmMsg}</p>
        <div className="flex gap-2 justify-end">
          <button
            className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-200 transition-colors"
            onClick={() => toast.dismiss(tId)}
          >
            Đóng
          </button>
          <button
            className={`px-3 py-1.5 text-white rounded-md text-sm font-medium transition-colors ${status === "IN_PROGRESS" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-red-600 hover:bg-red-700"
              }`}
            onClick={async () => {
              toast.dismiss(tId);
              try {
                await updateWorkOrderStatus(wo._id, status);
                toast.success(status === "IN_PROGRESS" ? "Đã bắt đầu thực hiện lệnh bảo trì" : "Đã hủy lệnh bảo trì thành công");
                loadWorkOrders();
              } catch (err) {
                toast.error(err?.response?.data?.message || "Không thể cập nhật trạng thái");
              }
            }}
          >
            Đồng ý
          </button>
        </div>
      </div>,
      { autoClose: false, closeOnClick: false, closeButton: false }
    );
  };

  const handleCompleteSubmit = async () => {
    if (!completeModal) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("notes", completeForm.notes || "");
      fd.append("cost", completeForm.cost || "0");
      completeForm.imagesAfter.forEach((f) => fd.append("imagesAfter", f));
      await completeWorkOrder(completeModal._id, fd);
      toast.success("Hoàn tất bảo trì! Thiết bị đã về trạng thái Khả dụng.");
      setCompleteModal(null);
      setCompleteForm({ notes: "", cost: "", imagesAfter: [] });
      loadWorkOrders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không thể hoàn tất");
    } finally {
      setSubmitting(false);
    }
  };

  // Pending reminders count badge
  const pendingCount = reminders.length;

  // Filtered WO
  const filteredWOs = useMemo(() => workOrders, [workOrders]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 font-display tracking-tight">
          Bảo trì thiết bị
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Quản lý lịch bảo trì định kỳ và lệnh sửa chữa theo từng thiết bị vật lý.
        </p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          label="Chờ xử lý"
          value={woStats.pending}
          icon={FiBell}
          color="text-amber-600"
          bg="bg-amber-50"
          trend="Cần ưu tiên"
        />
        <StatCard
          label="Đang thực hiện"
          value={woStats.inProgress}
          icon={FiLoader}
          color="text-blue-600"
          bg="bg-blue-50"
          trend="Đang làm"
        />
        <StatCard
          label="Đã hoàn tất"
          value={woStats.completed}
          icon={FiCheckCircle}
          color="text-emerald-600"
          bg="bg-emerald-50"
          trend="Thành công"
        />
        <StatCard
          label="Tổng chi phí"
          value={new Intl.NumberFormat("vi-VN").format(woStats.totalCost) + "đ"}
          icon={FiClipboard}
          color="text-indigo-600"
          bg="bg-indigo-50"
          trend="Tổng chi"
        />
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm mt-4">
        <div className="flex p-1 bg-slate-100 rounded-xl">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${isActive
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                  }`}
              >
                {tab.label}
                {tab.key === "reminders" && pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-black bg-rose-500 text-white">
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === "work_orders" && (
          <div className="flex items-center gap-3 px-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Trạng thái:</span>
              <select
                value={woStatusFilter}
                onChange={(e) => setWoStatusFilter(e.target.value)}
                className="text-xs font-bold text-slate-600 bg-transparent focus:outline-none cursor-pointer hover:text-indigo-600 transition-colors"
              >
                <option value="ALL">Tất cả</option>
                <option value="PENDING">Chờ xử lý</option>
                <option value="IN_PROGRESS">Đang làm</option>
                <option value="COMPLETED">Đã xong</option>
              </select>
            </div>
            <div className="w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Loại:</span>
              <select
                value={woTypeFilter}
                onChange={(e) => setWoTypeFilter(e.target.value)}
                className="text-xs font-bold text-slate-600 bg-transparent focus:outline-none cursor-pointer hover:text-indigo-600 transition-colors"
              >
                <option value="ALL">Tất cả</option>
                <option value="PREVENTIVE">Phòng ngừa</option>
                <option value="CORRECTIVE">Sửa chữa</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* ── Tab: Reminders ─────────────────────────────────────────────────── */}
      {activeTab === "reminders" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {remindersLoading
                ? "Đang tải..."
                : `${pendingCount} nhắc nhở đang chờ xử lý`}
            </p>
            <button
              onClick={loadReminders}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 border border-slate-200 hover:bg-slate-50"
            >
              <FiRefreshCw size={13} />
              Tải lại
            </button>
          </div>

          {remindersLoading ? (
            <LoadingSpinner />
          ) : reminders.length === 0 ? (
            <EmptyState
              icon={FiBell}
              title="Không có nhắc nhở nào"
              desc="Hệ thống sẽ tự động tạo nhắc nhở khi thiết bị đến lịch bảo trì."
            />
          ) : (
            reminders.map((rem) => (
              <ReminderCard
                key={rem._id}
                reminder={rem}
                onApprove={() => {
                  setApproveModal(rem);
                  setApproveForm({ scheduledDate: today(), notes: "" });
                }}
                onIgnore={() => handleIgnore(rem)}
              />
            ))
          )}
        </div>
      )}

      {/* ── Tab: Work Orders ───────────────────────────────────────────────── */}
      {activeTab === "workorders" && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {/* Status filter */}
              <select
                value={woStatusFilter}
                onChange={(e) => setWoStatusFilter(e.target.value)}
                className="rounded-xl border border-slate-200 text-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">Tất cả trạng thái</option>
                {Object.entries(WO_STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              {/* Type filter */}
              <select
                value={woTypeFilter}
                onChange={(e) => setWoTypeFilter(e.target.value)}
                className="rounded-xl border border-slate-200 text-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">Tất cả loại</option>
                {Object.entries(WO_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <button
                onClick={loadWorkOrders}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                <FiRefreshCw size={13} />
                Tải lại
              </button>
            </div>
            <button
              onClick={() => {
                setCreateWoModal(true);
                loadSupplierDeviceItems();
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
            >
              <FiPlus size={15} />
              Tạo lệnh thủ công
            </button>
          </div>

          {woLoading ? (
            <LoadingSpinner />
          ) : filteredWOs.length === 0 ? (
            <EmptyState
              icon={FiClipboard}
              title="Chưa có lệnh bảo trì nào"
              desc="Bạn có thể tạo lệnh thủ công hoặc duyệt từ nhắc nhở bảo trì."
            />
          ) : (
            <div className="space-y-4">
              {filteredWOs.map((wo) => (
                <WorkOrderCard
                  key={wo._id}
                  wo={wo}
                  onStart={() => handleUpdateStatus(wo, "IN_PROGRESS")}
                  onCancel={() => handleUpdateStatus(wo, "CANCELLED")}
                  onComplete={() => {
                    setCompleteModal(wo);
                    setCompleteForm({ notes: "", cost: "", imagesAfter: [] });
                  }}
                  onImageClick={setLightboxImg}
                />
              ))}

              {woTotalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8 py-4">
                  <button
                    onClick={() => setWoCurrentPage(p => Math.max(1, p - 1))}
                    disabled={woCurrentPage === 1}
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      if (woTotalPages <= 7) {
                        for (let i = 1; i <= woTotalPages; i++) pages.push(i);
                      } else {
                        if (woCurrentPage <= 4) {
                          pages.push(1, 2, 3, 4, 5, '...', woTotalPages);
                        } else if (woCurrentPage >= woTotalPages - 3) {
                          pages.push(1, '...', woTotalPages - 4, woTotalPages - 3, woTotalPages - 2, woTotalPages - 1, woTotalPages);
                        } else {
                          pages.push(1, '...', woCurrentPage - 1, woCurrentPage, woCurrentPage + 1, '...', woTotalPages);
                        }
                      }

                      return pages.map((page, index) => (
                        <button
                          key={index}
                          onClick={() => page !== '...' && setWoCurrentPage(page)}
                          disabled={page === '...'}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${page === '...'
                            ? 'text-slate-400 cursor-default'
                            : woCurrentPage === page
                              ? 'bg-indigo-600 text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </div>

                  <button
                    onClick={() => setWoCurrentPage(p => Math.min(woTotalPages, p + 1))}
                    disabled={woCurrentPage === woTotalPages}
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Approve Reminder ──────────────────────────────────────── */}
      {approveModal && (
        <Modal
          title="Duyệt nhắc nhở — Tạo lệnh bảo trì"
          onClose={() => setApproveModal(null)}
        >
          <div className="space-y-4">
            <InfoRow
              label="Thiết bị"
              value={`${approveModal.deviceId?.name || "—"} · ${approveModal.deviceItemId?.internalCode || approveModal.deviceItemId?.serialNumber || "N/A"
                }`}
            />
            <InfoRow label="Lý do nhắc" value={approveModal.triggerValue} />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Ngày thực hiện <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                min={today()}
                value={approveForm.scheduledDate}
                onChange={(e) => setApproveForm((f) => ({ ...f, scheduledDate: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
              <textarea
                rows={3}
                value={approveForm.notes}
                onChange={(e) => setApproveForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Nội dung cần bảo trì, lưu ý..."
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Thiết bị sẽ chuyển sang trạng thái <strong>Đang bảo trì</strong> và không thể cho thuê.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => setApproveModal(null)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Hủy
            </button>
            <button
              onClick={handleApproveSubmit}
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <FiLoader size={13} className="animate-spin" />}
              Xác nhận tạo lệnh
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Create WorkOrder ──────────────────────────────────────── */}
      {createWoModal && (
        <Modal
          title="Tạo lệnh bảo trì thủ công"
          maxWidth="max-w-2xl"
          onClose={() => {
            setCreateWoModal(false);
            setCreateWoForm({
              deviceItemId: "",
              maintenanceType: "PREVENTIVE",
              scheduledDate: today(),
              notes: "",
              priority: "LOW",
              estimatedCost: "",
              providerName: "",
              imagesBefore: [],
            });
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cột trái */}
            <div className="space-y-4">
              {/* Dropdown Thiết bị */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Thiết bị <span className="text-rose-500">*</span>
                </label>
                {deviceItemsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                    <FiTool size={13} className="animate-spin" /> Đang tải danh sách thiết bị...
                  </div>
                ) : allDeviceItems.length === 0 ? (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    ⚠️ Không tìm thấy thiết bị nào trong kho.
                  </p>
                ) : (
                  <DeviceSelector
                    items={allDeviceItems}
                    value={createWoForm.deviceItemId}
                    onChange={(val) => {
                      const selectedItem = allDeviceItems.find((i) => i._id === val);
                      const isIssue = selectedItem && selectedItem.status !== "AVAILABLE";
                      setCreateWoForm((f) => ({
                        ...f,
                        deviceItemId: val,
                        maintenanceType: isIssue ? "CORRECTIVE" : "PREVENTIVE",
                        priority: isIssue ? "HIGH" : "LOW",
                        notes: isIssue && (!f.notes || f.notes.trim() === "")
                          ? `Sửa chữa thiết bị do gặp sự cố (Trạng thái: ${selectedItem.status === "REPAIR" ? "Cần sửa chữa" : selectedItem.status === "DAMAGED" ? "Hư hỏng" : "Không khả dụng"})`
                          : f.notes
                      }));
                    }}
                    disabled={submitting}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Loại bảo trì</label>
                <select
                  value={createWoForm.maintenanceType}
                  onChange={(e) => setCreateWoForm((f) => ({ ...f, maintenanceType: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="PREVENTIVE">Phòng ngừa</option>
                  <option value="CORRECTIVE">Sửa chữa (Sự cố)</option>
                </select>
                {(() => {
                  const selected = allDeviceItems.find(i => i._id === createWoForm.deviceItemId);
                  if (selected && selected.status !== "AVAILABLE" && createWoForm.maintenanceType === "CORRECTIVE") {
                    return (
                      <p className="mt-1.5 text-[11px] text-rose-600 font-medium flex items-center gap-1">
                        <FiInfo size={12} className="flex-shrink-0" />
                        Hệ thống tự động chuyển sang Sửa chữa do thiết bị đang có sự cố.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Độ ưu tiên</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setCreateWoForm(f => ({ ...f, priority: k }))}
                      className={`py-1.5 text-xs font-medium rounded-lg border transition-all ${createWoForm.priority === k
                        ? PRIORITY_STYLES[k] + " border-current ring-1 ring-offset-1 ring-slate-100"
                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                        }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Cột phải */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Ngày thực hiện <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  min={today()}
                  value={createWoForm.scheduledDate}
                  onChange={(e) => setCreateWoForm((f) => ({ ...f, scheduledDate: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Chi phí dự kiến (đ)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={createWoForm.estimatedCost}
                    onChange={(e) => setCreateWoForm((f) => ({ ...f, estimatedCost: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Đơn vị thực hiện</label>
                  <input
                    type="text"
                    placeholder="Tên đơn vị/kỹ thuật..."
                    value={createWoForm.providerName}
                    onChange={(e) => setCreateWoForm((f) => ({ ...f, providerName: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
                <textarea
                  rows={2}
                  value={createWoForm.notes}
                  onChange={(e) => setCreateWoForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Nội dung cần thực hiện..."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 text-indigo-600 font-semibold">
                  Ảnh hiện trạng (Trước bảo trì)
                </label>
                <div className="flex flex-wrap gap-2">
                  {(createWoForm.imagesBefore || []).map((file, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                        alt=""
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setCreateWoForm((f) => ({
                            ...f,
                            imagesBefore: f.imagesBefore.filter((_, idx) => idx !== i),
                          }))
                        }
                        className="absolute -top-1 -right-1 bg-white text-rose-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiXCircle size={16} className="fill-white" />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded-lg bg-slate-50 border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-indigo-600">
                    <FiCamera size={16} />
                    <span className="text-[9px] mt-0.5 font-medium">Thêm</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const selectedFiles = Array.from(e.target.files);
                          setCreateWoForm((f) => ({
                            ...f,
                            imagesBefore: [...(f.imagesBefore || []), ...selectedFiles],
                          }));
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Thiết bị sẽ chuyển sang trạng thái <strong>Đang bảo trì</strong> và tạm dừng cho thuê.
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => setCreateWoModal(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Hủy
            </button>
            <button
              onClick={handleCreateWo}
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <FiLoader size={13} className="animate-spin" />}
              Tạo lệnh
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Complete WorkOrder ──────────────────────────────────────── */}
      {completeModal && (
        <Modal
          title={completeModal.status === "INFO_REQUIRED" ? "Gửi lại báo cáo bảo trì" : "Xác nhận hoàn tất bảo trì"}
          maxWidth="max-w-lg"
          onClose={() => setCompleteModal(null)}
        >
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
              {completeModal.deviceId?.images?.[0] && (
                <img src={completeModal.deviceId.images[0]} className="w-10 h-10 rounded-lg object-cover" alt="" />
              )}
              <div>
                <p className="text-sm font-bold text-slate-900">{completeModal.deviceId?.name || "Thiết bị"}</p>
                <p className="text-xs text-slate-500">{completeModal.deviceItemId?.internalCode || completeModal.deviceItemId?.serialNumber || "N/A"}</p>
              </div>
            </div>

            {/* Review Before Images */}
            {completeModal.imagesBefore?.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Đối chiếu ảnh hiện trạng (Trước khi làm)
                </label>
                <div className="flex flex-wrap gap-2">
                  {completeModal.imagesBefore.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      className="w-16 h-16 object-cover rounded-lg border border-slate-200 shadow-sm cursor-zoom-in hover:border-indigo-400 transition-colors"
                      alt="Trước"
                      onClick={() => setLightboxImg(img)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Ảnh kết quả sau bảo trì <span className="text-rose-500">*</span>
              </label>
              <div className="flex flex-wrap gap-3">
                {(completeForm.imagesAfter || []).map((file, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      className="w-20 h-20 object-cover rounded-xl border border-slate-200 shadow-sm"
                      alt=""
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setCompleteForm((f) => ({
                          ...f,
                          imagesAfter: f.imagesAfter.filter((_, idx) => idx !== i),
                        }))
                      }
                      className="absolute -top-2 -right-2 bg-white text-rose-500 rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FiXCircle size={18} className="fill-white" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-indigo-600 group">
                  <FiCamera size={20} className="group-hover:scale-110 transition-transform" />
                  <span className="text-[10px] mt-1 font-medium">Thêm ảnh</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const selectedFiles = Array.from(e.target.files);
                        setCompleteForm((f) => ({
                          ...f,
                          imagesAfter: [...(f.imagesAfter || []), ...selectedFiles],
                        }));
                      }
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Chi phí thực tế (đ)</label>
                <input
                  type="number"
                  min={0}
                  value={completeForm.cost}
                  onChange={(e) => setCompleteForm((f) => ({ ...f, cost: e.target.value }))}
                  placeholder="0"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú kết quả</label>
                <textarea
                  rows={3}
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder={completeModal.status === "INFO_REQUIRED" ? "Nhập thông tin bổ sung, giải trình theo yêu cầu..." : "Mô tả công việc đã hoàn thành..."}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
              <p className="text-xs text-emerald-700 leading-relaxed">
                ✨ <strong>Thông báo:</strong> Lệnh bảo trì của bạn sẽ được chuyển đến Admin để <strong>Nghiệm thu</strong>
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setCompleteModal(null)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Để sau
            </button>
            <button
              onClick={handleCompleteSubmit}
              disabled={submitting}
              className="px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-emerald-100"
            >
              {submitting && <FiLoader size={13} className="animate-spin" />}
              {completeModal.status === "INFO_REQUIRED" ? "Cập nhật báo cáo" : "Hoàn tất bảo trì"}
            </button>
          </div>
        </Modal>
      )}

      {/* Lightbox */}
      {lightboxImg && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg text-slate-600 hover:text-rose-500"
            >
              <FiXCircle size={18} />
            </button>
            <img src={lightboxImg} alt="Ảnh bảo trì" className="rounded-xl max-h-[85vh] object-contain" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ReminderCard({ reminder, onApprove, onIgnore }) {
  const item = reminder.deviceItemId || {};
  const device = reminder.deviceId || {};

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      {/* Device image */}
      {device.images?.[0] ? (
        <img
          src={device.images[0]}
          alt={device.name}
          className="w-16 h-16 rounded-xl object-cover border border-slate-200 shrink-0"
        />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
          <FiTool size={24} className="text-slate-400" />
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-semibold text-slate-900 text-sm">
            {device.name || "Thiết bị"}
          </span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-lg">
            {item.internalCode || item.serialNumber || "N/A"}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
            {TRIGGER_TYPE_LABELS[reminder.triggerType] || reminder.triggerType}
          </span>
        </div>
        <p className="text-sm text-slate-600">{reminder.triggerValue}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          Nhắc lúc {new Date(reminder.createdAt).toLocaleString("vi-VN")}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 shrink-0">
        <button
          onClick={onIgnore}
          className="px-3 py-1.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Bỏ qua
        </button>
        <button
          onClick={onApprove}
          className="px-4 py-1.5 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
        >
          <FiCheckCircle size={13} />
          Duyệt → Tạo lệnh
        </button>
      </div>
    </div>
  );
}

function WorkOrderCard({ wo, onStart, onCancel, onComplete, onImageClick }) {
  const [expanded, setExpanded] = useState(false);
  const item = wo.deviceItemId || {};
  const device = wo.deviceId || {};

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-3 p-5 cursor-pointer"
        onClick={() => setExpanded((e) => !e)}
      >
        {/* Device image */}
        {device.images?.[0] ? (
          <img
            src={device.images[0]}
            alt={device.name}
            className="w-14 h-14 rounded-xl object-cover border border-slate-200 shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <FiTool size={20} className="text-slate-400" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {/* Status */}
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${WO_STATUS_STYLES[wo.status] || "bg-slate-50 text-slate-600 border-slate-200"
                }`}
            >
              {WO_STATUS_LABELS[wo.status] || wo.status}
            </span>
            {/* Type */}
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${WO_TYPE_STYLES[wo.maintenanceType] || "bg-slate-50 text-slate-600"
                }`}
            >
              {WO_TYPE_LABELS[wo.maintenanceType] || wo.maintenanceType}
            </span>
            {/* Priority */}
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${PRIORITY_STYLES[wo.priority] || "bg-slate-50 text-slate-500"
                }`}
            >
              Uu tiên: {PRIORITY_LABELS[wo.priority] || wo.priority}
            </span>
          </div>
          <p className="font-semibold text-slate-900 text-sm">
            {device.name || "Thiết bị"}{" "}
            <span className="text-slate-500 font-normal text-xs">
              · {item.internalCode || item.serialNumber || "N/A"}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <FiCalendar size={11} />
              Lên lịch: {formatDate(wo.scheduledDate)}
            </span>
            {wo.providerName && (
              <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 text-slate-500">
                <FiTool size={11} />
                {wo.providerName}
              </span>
            )}
            {wo.completedDate && (
              <span className="flex items-center gap-1">
                <FiCheckCircle size={11} className="text-green-500" />
                Hoàn tất: {formatDate(wo.completedDate)}
              </span>
            )}
          </div>
        </div>

        {/* Expand toggle */}
        <div className="shrink-0 text-slate-400">
          {expanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          {/* Notes */}
          {wo.notes && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Ghi chú</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{wo.notes}</p>
            </div>
          )}

          {/* Cost & Provider Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {wo.providerName && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Đơn vị thực hiện</p>
                <p className="text-sm text-slate-700 font-medium">{wo.providerName}</p>
              </div>
            )}
            {(wo.estimatedCost > 0 || (wo.status === "COMPLETED" && wo.cost > 0)) && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Chi phí</p>
                <div className="flex items-center gap-3">
                  {wo.estimatedCost > 0 && (
                    <div className="text-sm">
                      <span className="text-slate-400">Dự kiến:</span>{" "}
                      <span className="font-medium text-slate-600">{new Intl.NumberFormat("vi-VN").format(wo.estimatedCost)}₫</span>
                    </div>
                  )}
                  {wo.status === "COMPLETED" && wo.cost > 0 && (
                    <div className="text-sm">
                      <span className="text-slate-400">Thực tế:</span>{" "}
                      <span className="font-bold text-indigo-600">{new Intl.NumberFormat("vi-VN").format(wo.cost)}₫</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Images */}
          {(wo.imagesBefore?.length > 0 || wo.imagesAfter?.length > 0) && (
            <div className="grid grid-cols-2 gap-4">
              {wo.imagesBefore?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    Ảnh trước
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {wo.imagesBefore.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => onImageClick(img)}
                        className="h-16 w-16 rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-300"
                      >
                        <img src={img} alt="Trước" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {wo.imagesAfter?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    Ảnh sau
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {wo.imagesAfter.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => onImageClick(img)}
                        className="h-16 w-16 rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-300"
                      >
                        <img src={img} alt="Sau" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {wo.status !== "COMPLETED" && wo.status !== "CANCELLED" && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              {wo.status === "PENDING" && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onStart(); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    ▶️ Bắt đầu thực hiện
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onCancel(); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <FiXCircle size={13} />
                    Hủy lệnh
                  </button>
                </>
              )}
              {(wo.status === "IN_PROGRESS" || wo.status === "INFO_REQUIRED") && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onComplete(); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    <FiCheckCircle size={13} />
                    {wo.status === "INFO_REQUIRED" ? "Gửi lại kết quả" : "Đánh dấu hoàn tất"}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onCancel(); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <FiXCircle size={13} />
                    Hủy lệnh
                  </button>
                </>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="text-xs text-slate-400">
            <span>Tạo lúc: {formatDateTime(wo.createdAt)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, maxWidth = "max-w-md" }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <FiXCircle size={18} />
          </button>
        </div>
        <div className="px-5 pt-4 pb-2">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-sm font-medium text-slate-500 shrink-0">{label}:</span>
      <span className="text-sm text-slate-800">{value || "—"}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-12 text-center">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 mb-4">
        <Icon size={28} />
      </div>
      <p className="text-slate-700 font-medium mb-1">{title}</p>
      <p className="text-sm text-slate-500 max-w-xs mx-auto">{desc}</p>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="h-8 w-8 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, bg, trend }) {
  return (
    <div className={`p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all group overflow-hidden relative`}>
      <div className={`absolute top-0 right-0 w-24 h-24 ${bg} opacity-10 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110`} />
      <div className="flex items-center gap-4 relative z-10">
        <div className={`w-12 h-12 rounded-2xl ${bg} ${color} flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={24} />
        </div>
        <div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
          <div className="flex items-baseline gap-2">
            <p className={`text-xl font-black text-slate-900`}>{value}</p>
            {trend && <span className="text-[10px] font-medium text-slate-400">{trend}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
